import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import SEO from "@/components/cre/SEO";

/**
 * Reusable SEO landing-page layout. Renders:
 *  - SEO head tags + structured data
 *  - Hero with H1 + intro
 *  - Problem statement
 *  - How CREAIOps helps
 *  - Key benefits grid
 *  - Enterprise use case
 *  - FAQ section
 *  - CTA + internal links to related SEO pages
 *
 * Pass page-specific copy via props. Keep wording grounded — advisory only,
 * human-in-the-loop, no auto-remediation promises.
 */
export default function SeoLandingLayout({
  slug,
  metaTitle,
  metaDescription,
  h1,
  intro,
  problem,
  howWeHelp,
  benefits,
  useCase,
  faq,
  related,
  ctaPrimary = { label: "Try CRE without login", to: "/preview" },
  ctaSecondary = { label: "Back to homepage", to: "/" },
}) {
  const canonical = `https://creaiops.com/${slug}`;

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      data-testid={`seo-page-${slug}`}
    >
      <SEO
        title={metaTitle}
        description={metaDescription}
        canonical={canonical}
        faq={faq}
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cre-hero-aurora pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8 md:pb-12">
          <div className="cre-pill cre-pill-grad">
            <ShieldCheck className="h-3 w-3" strokeWidth={2} />
            AI Decision Intelligence for IT Operations
          </div>

          <h1
            data-testid="seo-h1"
            className="mt-5 font-heading font-black tracking-tighter text-3xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-4xl"
          >
            {h1}
          </h1>

          <p
            data-testid="seo-intro"
            className="mt-4 md:mt-5 text-sm sm:text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl"
          >
            {intro}
          </p>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <Link to={ctaPrimary.to} data-testid="seo-cta-primary">
              <Button
                size="lg"
                className="cre-cta-primary rounded-lg font-semibold px-5 h-11"
              >
                {ctaPrimary.label}
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </Link>
            <Link to={ctaSecondary.to} data-testid="seo-cta-secondary">
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold px-5 h-11"
              >
                {ctaSecondary.label}
              </Button>
            </Link>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 text-[12px] text-slate-500 font-mono">
            <ShieldCheck
              className="h-3.5 w-3.5 text-emerald-600"
              strokeWidth={2}
            />
            Advisory only. Human approval required before any action.
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      {problem ? (
        <section className="border-t border-slate-200 bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="cre-overline text-sky-700">The problem</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              {problem.title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed max-w-3xl">
              {problem.body}
            </p>
            {problem.points && problem.points.length ? (
              <ul className="mt-5 space-y-2 max-w-3xl">
                {problem.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-slate-700"
                  >
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* HOW CREAIOPS HELPS */}
      {howWeHelp ? (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="cre-overline text-sky-700">How CREAIOps helps</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              {howWeHelp.title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed max-w-3xl">
              {howWeHelp.body}
            </p>
            <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {howWeHelp.items.map((it) => (
                <div key={it.title} className="cre-card p-4 md:p-6">
                  <div className="font-heading font-bold tracking-tight text-slate-900">
                    {it.title}
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {it.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* KEY BENEFITS */}
      {benefits && benefits.length ? (
        <section className="border-t border-slate-200 bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="cre-overline text-sky-700">Key benefits</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Outcomes teams see with CREAIOps
            </h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {benefits.map((b) => (
                <li
                  key={b}
                  className="cre-card p-4 md:p-5 flex items-start gap-3"
                >
                  <CheckCircle2
                    className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0"
                    strokeWidth={2}
                  />
                  <span className="text-sm md:text-base text-slate-800 leading-relaxed">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {/* ENTERPRISE USE CASE */}
      {useCase ? (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="cre-overline text-sky-700">Enterprise use case</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              {useCase.title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed max-w-3xl">
              {useCase.body}
            </p>
            {useCase.steps && useCase.steps.length ? (
              <ol className="mt-5 space-y-3 max-w-3xl list-decimal list-inside">
                {useCase.steps.map((s) => (
                  <li
                    key={s}
                    className="text-sm md:text-base text-slate-700 leading-relaxed"
                  >
                    {s}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* FAQ */}
      {faq && faq.length ? (
        <section className="border-t border-slate-200 bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="cre-overline text-sky-700">FAQ</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Frequently asked questions
            </h2>
            <div className="mt-6 space-y-4">
              {faq.map((item) => (
                <div
                  key={item.q}
                  className="cre-card p-4 md:p-6"
                  data-testid="seo-faq-item"
                >
                  <h3 className="font-heading font-bold tracking-tight text-slate-900 text-base md:text-lg">
                    {item.q}
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA + RELATED LINKS */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="cre-card p-6 md:p-8 bg-gradient-to-br from-white to-sky-50/40 border-sky-200">
            <h2 className="font-heading font-black tracking-tighter text-2xl md:text-3xl text-slate-900">
              See CREAIOps reason through your next incident
            </h2>
            <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl">
              Try the AI incident reasoning workspace with anonymized inputs.
              No setup. No card. Human approval stays with you.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Link to="/preview" data-testid="seo-bottom-cta-preview">
                <Button
                  size="lg"
                  className="cre-cta-primary rounded-lg font-semibold px-5 h-11"
                >
                  Try CRE without login
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                </Button>
              </Link>
              <Link to="/" data-testid="seo-bottom-cta-home">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold px-5 h-11"
                >
                  Explore the homepage
                </Button>
              </Link>
            </div>
          </div>

          {related && related.length ? (
            <div className="mt-8">
              <div className="cre-overline text-slate-500">Related reading</div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {related.map((r) => (
                  <li key={r.to}>
                    <Link
                      to={r.to}
                      data-testid={`seo-related-${r.to.replace(/^\//, "")}`}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition-colors"
                    >
                      {r.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
}
